import { useCallback, useEffect, useRef } from 'react';

import { AudioPlayerStreamingManager } from '../effects/AudioPlayerStreamingManager';

import { useAudioPlayerStreamingStore } from './useAudioPlayerStreamingStore';

type AudioPlayerStreamingConfig<TRequest, TResponse> = {
	fetchQuery: () => Promise<TResponse>;
};

export function useAudioPlayerStreaming<TRequest, TResponse extends ReadableStream>({
	fetchQuery,
}: AudioPlayerStreamingConfig<TRequest, TResponse>) {
	const { state, setAudioPlayerStreamingState } = useAudioPlayerStreamingStore();

	const audioElementRef = useRef<HTMLAudioElement>(null);
	const audioPlayerStreamingManager = useRef(
		new AudioPlayerStreamingManager<TRequest, TResponse>({
			fetchQuery,
			onAudioStreamingChange: (action) => setAudioPlayerStreamingState(action),
		}),
	);

	const fetchAudio = useCallback(async () => {
		await audioPlayerStreamingManager.current.fetchAudio();
		audioPlayerStreamingManager.current.streamAudioToMediaSource(audioElementRef.current);
	}, [audioPlayerStreamingManager]);

	useEffect(() => {
		return () => {
			// eslint-disable-next-line react-hooks/exhaustive-deps
			audioPlayerStreamingManager.current.disposeStream();
		};
	}, []);

	return { state, fetchAudio, audioElementRef };
}
